import { Loader2, CheckCircle2, XCircle, AlertTriangle, Sparkles, RefreshCcw } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { fmtCompactRelative } from "@/lib/format"
import type { RehearsalSummary } from "@/types/test_lab"

type Props = {
  rehearsal: RehearsalSummary | null
  skillId: number
  draftVersionNumber: number
  onRunRehearsal: () => void
  isStarting: boolean
  // Reset failed/errored runs to pending and re-flip the rehearsal to running.
  onRerunFailed?: () => void
  isRerunning?: boolean
}

// Shown above the scenario list on the per-skill TestLab Test tab.
// One of four states: never-rehearsed / running / passed / failed-or-errored.
// Standard tone — gold is reserved for Forge surfaces.
export function RehearsalCardCompact({ rehearsal, skillId, draftVersionNumber, onRunRehearsal, isStarting, onRerunFailed, isRerunning }: Props) {
  const state = rehearsal?.state ?? null
  const isRunning = isStarting || isRerunning || state === "queued" || state === "running"
  const failedTotal = (rehearsal?.failedCount ?? 0) + (rehearsal?.erroredCount ?? 0)
  const canRerunFailed =
    !!onRerunFailed && !!rehearsal && (state === "failed" || state === "errored") && failedTotal > 0

  return (
    <Card className="flex flex-wrap items-center gap-3 px-4 py-3">
      <Icon state={state} isStarting={isStarting} />

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-app-text">
            Rehearsal — agent suite vs. v{draftVersionNumber} draft
          </span>
          {rehearsal && (
            <Counts r={rehearsal} />
          )}
        </div>
        <p className="mt-0.5 text-xs text-app-muted">
          <Copy state={state} rehearsal={rehearsal} isStarting={isStarting} />
        </p>
      </div>

      <div className="flex items-center gap-2">
        {rehearsal && (
          <a
            href={`/testing#rehearsal-${rehearsal.id}`}
            className="rounded-control border border-border px-3 py-1.5 text-xs text-app-text transition hover:bg-white/5"
          >
            View full results
          </a>
        )}
        {canRerunFailed && (
          <Button size="sm" variant="ghost" onClick={onRerunFailed} disabled={!!isRerunning}>
            <RefreshCcw size={14} className="mr-1" />
            {isRerunning ? "Rerunning…" : `Rerun failed (${failedTotal})`}
          </Button>
        )}
        {!isRunning && (
          <Button size="sm" variant="ghost" onClick={onRunRehearsal} disabled={isStarting}>
            <Sparkles size={14} className="mr-1" />
            {rehearsal ? "Re-run rehearsal" : "Run rehearsal"}
          </Button>
        )}
      </div>
      {/* Skill id surfaced for debugging deeplinks; not visible. */}
      <span className="hidden" data-skill-id={skillId} />
    </Card>
  )
}

function Icon({ state, isStarting }: { state: string | null; isStarting: boolean }) {
  if (isStarting || state === "queued" || state === "running")
    return <Loader2 size={20} className="animate-spin text-app-muted" />
  if (state === "passed")  return <CheckCircle2 size={20} className="text-status-success" />
  if (state === "failed")  return <XCircle size={20} className="text-status-error" />
  if (state === "errored") return <AlertTriangle size={20} className="text-status-warning" />
  return <Sparkles size={20} className="text-app-muted" />
}

function Counts({ r }: { r: RehearsalSummary }) {
  return (
    <span className="flex flex-wrap gap-2 text-[11px]">
      <span className="text-status-success">✓ {r.passedCount}</span>
      {r.failedCount  > 0 && <span className="text-status-error">✗ {r.failedCount}</span>}
      {r.erroredCount > 0 && <span className="text-status-warning">⚠ {r.erroredCount}</span>}
      <span className="text-app-muted">/ {r.total}</span>
    </span>
  )
}

function Copy({ state, rehearsal, isStarting }:
  { state: string | null; rehearsal: RehearsalSummary | null; isStarting: boolean }) {
  if (isStarting) return <>Queueing rehearsal…</>
  if (!rehearsal) return <>Run the agent's full scenario suite against this draft before promoting.</>
  if (state === "queued" || state === "running")
    return <>Running across the agent suite — refreshes automatically.</>
  if (state === "passed")
    return <>All clear · finished {fmtCompactRelative(rehearsal.finishedAt)}.</>
  if (state === "failed")
    return <>{rehearsal.failedCount} failure{rehearsal.failedCount === 1 ? "" : "s"} block promotion · finished {fmtCompactRelative(rehearsal.finishedAt)}.</>
  if (state === "errored")
    return <>Errored before completion · finished {fmtCompactRelative(rehearsal.finishedAt)}.</>
  return <>Finished {fmtCompactRelative(rehearsal.finishedAt)}.</>
}
